import React, { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

import { showNotification } from "../../../../../constants";
import { uploadSalesImage } from "../../hooks/SalesPagesHook";

/**
 * Asset upload field for the Sales CMS: uploads to Firebase Storage and stores
 * the resulting download URL. Also accepts a pasted URL.
 *
 * Props:
 * - accept: input `accept` filter (default "image/*").
 * - isImage: if true, render an <img> preview (default true).
 * - noun: label noun for the button/messages, e.g. "Image" or "File".
 */
const ImageUploadField = ({
  label,
  value,
  page,
  disabled,
  onChange,
  accept = "image/*",
  isImage = true,
  noun = "Image",
}) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (isImage && !file.type.startsWith("image/")) {
      showNotification("Please select an image file");
      return;
    }
    try {
      setUploading(true);
      const url = await uploadSalesImage(file, page);
      onChange(url);
    } catch (error) {
      console.log("Error uploading file:", error.message);
      showNotification(`Error uploading ${noun.toLowerCase()}`);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={labelStyles}>{label}</Typography>
      {value && isImage ? (
        <Box sx={{ mb: 1 }}>
          <img src={value} alt={label} style={previewStyles} />
        </Box>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          variant="outlined"
          component="label"
          size="small"
          disabled={disabled || uploading}
          style={buttonStyles}
        >
          {uploading ? (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          ) : null}
          {value ? `Replace ${noun}` : `Upload ${noun}`}
          <input hidden type="file" accept={accept} onChange={handleFile} />
        </Button>
        {value && !disabled ? (
          <Button
            variant="text"
            size="small"
            style={{ textTransform: "none", color: "#B00020" }}
            onClick={() => onChange("")}
          >
            Remove
          </Button>
        ) : null}
      </Box>
      {value ? (
        <Typography sx={urlStyles} noWrap title={value}>
          {value}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ImageUploadField;

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};

const urlStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 11,
  color: "#808080",
  mt: 0.5,
  maxWidth: 500,
};

const buttonStyles = {
  textTransform: "none",
  color: "#00A7CD",
  borderColor: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};

const previewStyles = {
  maxWidth: 220,
  maxHeight: 140,
  borderRadius: 8,
  objectFit: "cover",
  border: "1px solid #E0E0E0",
};
